import { Injectable } from "@angular/core";
import { Http, Headers, Response, RequestOptions } from "@angular/http";
import { Observable } from "rxjs";
import "rxjs/add/operator/map";
import "rxjs/add/operator/catch";
import "rxjs/add/observable/of";
import { environment } from "../../environments/environment";
import { Agreement, IAgreement } from "../_models/agreement";
import { Car } from "../_models/car";
import { AuthenticationService } from "../_services/authentication.service";
import { EnvironmentService } from "./index";
import { Dealer } from "app/_models/dealer";
import { Customer } from "app/_models/customer";
import { MatrixCarService } from "./matrix/matrix-cars.service";
import { UsedCarAgreementService } from "./matrix/products/used-cars-agreement.service";
import { ProlongingsAgreementService } from "./matrix/products/prolongings-agreement.service";
import { KmCatchUpAgreementService } from "./matrix/products/km-catch-up-agreement.service";

@Injectable()
export class AgreementsService {
  private baseUrl: string = this.environmentService.getBaseURL() + "/api";

  constructor(
    private http: Http,
    private authenticationService: AuthenticationService,
    private environmentService: EnvironmentService,
    private prolongingsAgreementService: ProlongingsAgreementService,
    private usedCarAgreementService: UsedCarAgreementService,
    private kmCatchUpAgreementService: KmCatchUpAgreementService
  ) {}

  getAll(): Observable<Agreement[]> {
    let agreements$ = this.http
      .get(
        `${this.baseUrl}/serviceagreements?populate=customer&populate=car`,
        this.makeHeaders()
      )
      .map(mapAgreements)
      .catch(handleError);
    return agreements$;
  }

  get(id: string): Observable<Agreement> {
    let agreement$ = this.http
      .get(
        `${this.baseUrl}/serviceagreements/${id}?populate=employees&populate=cars`,
        this.makeHeaders()
      )
      .map(mapAgreement)
      .catch(handleError);
    return agreement$;
  }

  getForCustomer(customerId: string): Observable<Agreement[]> {
    let agreements$ = this.http
      .get(
        `${this.baseUrl}/customer-agreements/${customerId}`,
        this.makeHeaders()
      )
      .map(mapAgreements)
      .catch(handleError);
    return agreements$;
  }

  create(agreement: Partial<Agreement>): Observable<Agreement> {
    let newAgreement = this.http
      .post(
        `${this.baseUrl}/serviceagreements`,
        JSON.stringify(agreement),
        this.makeHeaders()
      )
      .map(mapAgreement);
    return newAgreement;
  }

  read(id: string): Observable<Agreement> {
    let agreement = this.http
      .get(`${this.baseUrl}/serviceagreements/${id}`, this.makeHeaders())
      .map(mapAgreement)
      .catch(handleError);
    return agreement;
  }

  update(agreement: Partial<Agreement>): Observable<Response> {
    return this.http.put(
      `${this.baseUrl}/serviceagreements/${agreement._id}`,
      JSON.stringify(agreement),
      this.makeHeaders()
    );
  }

  delete(id: string): Observable<Response> {
    return this.http
      .delete(`${this.baseUrl}/serviceagreements/${id}`, this.makeHeaders())
      .map((res: Response) => res.json()) // ...and calling .json() on the response to return data
      .catch((error: any) =>
        Observable.throw(error.json().error || "Server error")
      ); //...errors if any
  }

  getDealer(id: string, serviceType: string): Observable<IAgreement[]> {
    if (serviceType === "tyreHotel") {
      let agreements$ = this.http
        .get(
          `${this.baseUrl}/tyreagreements?status=true&dealer_id=${id}&populate=customer&populate=tyre&type=${serviceType}`,
          this.makeHeaders()
        )
        .map(mapAgreements)
        .catch((error: any) => {
          // Handle 404 (no tyre agreements found) gracefully
          if (error.status === 404) {
            console.log('No tyre agreements found for dealer:', id);
            return Observable.of([]); // Return empty array instead of error
          }
          // For other errors, use the standard error handling
          return handleError(error);
        });
      return agreements$;
    } else if (serviceType === "usedcars") {
      return this.usedCarAgreementService.getByDealer(id);
    } else if (serviceType === "prolongings") {
      return this.prolongingsAgreementService.getByDealer(id);
    } else if (serviceType === "kmcatchup") {
      return this.kmCatchUpAgreementService.getByDealer(id);
    } else {
      let agreements$ = this.http
        .get(
          `${this.baseUrl}/serviceagreements?status=true&dealer_id=${id}&populate=customer&populate=car&type=${serviceType}`,
          this.makeHeaders()
        )
        .map(mapAgreements)
        .catch((error: any) => {
          // Handle 404 (no service agreements found) gracefully
          if (error.status === 404) {
            console.log('No service agreements found for dealer:', id, 'type:', serviceType);
            return Observable.of([]); // Return empty array instead of error
          }
          // For other errors, use the standard error handling
          return handleError(error);
        });
      return agreements$;
    }
  }

  acceptAgreement(
    agreement: Agreement,
    dealer: Dealer,
    regnr: string,
    mauticId: string
  ): Observable<Response> {
    return this.http
      .get(
        `${this.baseUrl}/serviceagreements/${agreement._id}/accept-agreement?dealer=${dealer._id}&regnr=${regnr}&mauticId=${mauticId}`,
        this.makeHeaders()
      )
      .map((res: Response) => res.json())
      .catch((error: any) =>
        Observable.throw(error.json().error || "Server error")
      );
  }

  savePdf(agreement: any) {
    let newAgreement = this.http.post(
      `${this.baseUrl}/agreementPdf`,
      JSON.stringify(agreement),
      this.makeHeaders()
    );
    return newAgreement;
  }

  private makeHeaders(): RequestOptions {
    let headers = new Headers();

    headers.append("Authorization", this.authenticationService.getToken());
    headers.append("Content-Type", "application/json ");
    headers.append("charset", "UTF-8 ");
    headers.append("Accept", "application/json ");

    let options = new RequestOptions({ headers: headers });
    return options;
  }
}
function mapAgreements(response: Response): Agreement[] {
  // uncomment to simulate error:
  // throw new Error('ups! Force choke!');
  return response.json().map(toAgreement);
}

function toAgreement(r: any): Agreement {
  if (r.car != null) {
    let agreement = <Agreement>{
      _id: r._id,
      customer: r.customer,
      car: r.car,
      years: r.years,
      km: r.km,
      addons: r.addons,
      service_price: r.service_price,
      price: r.price,
      specialOffer: r.specialOffer,
      payout: r.payout,
      final_amount: r.final_amount,
      contract_date: r.contract_date,
      dealer_id: r.dealer_id,
      sellerInitials: r.sellerInitials,
      calculatorType: r.calculatorType,
      customerAccept: r.customerAccept,
      monthsToGo: r.monthsToGo,
      carMonthsOld: r.carMonthsOld,
    };

    return agreement;
  } else {
    //Map to tyreAgreement
    let agreement = <Agreement>{
      _id: r._id,
      customer: r.customer,
      addons: r.addons,
      final_amount: r.final_amount,
      contract_date: r.contract_date,
      dealer_id: r.dealer_id,
      sellerInitials: r.sellerInitials,
      //calculatorType: r.calculatorType,
      customerAccept: r.customerAccept,
      tyre: r.tyre,
      car: r.car,
    };

    //console.log(r);
    if (r.customer != null && r.customer.carmake != null) {
      let car = <Car>{
        carmake: r.customer.carmake,
        carmodel: r.customer.carmodel,
      };
      agreement.car = car;
    }

    return agreement;
  }
}

function mapAgreement(response: Response): Agreement {
  // toPerson looks just like in the previous example
  return toAgreement(response.json());
}

// this could also be a private method of the component class
function handleError(error: any) {
  // log error
  // could be something more sofisticated
  let errorMsg =
    error.message ||
    `Yikes! There was was a problem with our hyperdrive device and we couldn't retrieve your data!`;
  console.error(errorMsg);

  // throw an application level error
  return Observable.throw(errorMsg);
}



// WEBPACK FOOTER //
// ./src/app/_services/agreements.service.ts